import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity } from 'react-native';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MdCalendarToday } from 'react-icons/md';
import { IoNavigate } from 'react-icons/io5';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

// Web token helper
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

export default function CustomerJobs() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId');
  const customerName = searchParams.get('customerName') || 'Customer';

  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);

  // overlays (web-safe instead of RN Modal)
  const [completionOpen, setCompletionOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  // paging
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 25;

  const [userPermissions, setUserPermissions] = useState(null);

  // Load user permissions on component mount
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          setUserPermissions(JSON.parse(permissions));
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, []);

  // Check if user has job write permissions
  const hasJobWritePermission = () => {
    return userPermissions?.job_perm === 'write';
  };

  // Check if user can see prices
  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  const loadJobs = async (nextPage = 1) => {
    if (!customerId) return;
    try {
      setLoading(true);
      const token = getToken();
      const { data } = await axios.get(
        `${API_BASE_URL}/jobs/all?customerId=${encodeURIComponent(customerId)}&page=${nextPage}&page_size=${PAGE_SIZE}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      const list = Array.isArray(data) ? data : data?.jobs ?? data ?? [];
      setJobs(prev => (nextPage === 1 ? list : [...prev, ...list]));
      setHasMore(list.length >= PAGE_SIZE);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      alert('Failed to load jobs. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // reset and load when the customer changes
    setJobs([]);
    setPage(1);
    setHasMore(true);
    loadJobs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const handleLoadMore = () => {
    if (!hasMore || loading) return;
    const next = page + 1;
    setPage(next);
    loadJobs(next);
  };

  const handleCompletePress = (job) => {
    setSelectedJob(job);
    setCompletionOpen(true);
  };

  const handleCompletionConfirm = async () => {
    if (!selectedJob) return;
    try {
      const token = getToken();
      await axios.put(
        `${API_BASE_URL}/jobs/edit/${selectedJob.id}`,
        { ...selectedJob, completed: 'yes' },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      setCompletionOpen(false);
      
      // Update the job in the local state immediately
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === selectedJob.id 
            ? { ...job, completed: 'yes' }
            : job
        )
      );
      
      // Check permissions before showing quote/invoice creation modals
      if (selectedJob.visitType === 'quote') {
        // Only show quote modal if user has quote write permission
        if (userPermissions?.quote_perm === 'write') {
          setQuoteOpen(true);
        }
      } else {
        // Only show invoice modal if user has invoice write permission
        if (userPermissions?.invoice_perm === 'write') {
          setInvoiceOpen(true);
        }
      }
    } catch (err) {
      console.error('Error updating job completion:', err);
      alert('Failed to update job completion. Please try again later.');
    }
  };

  const handleInvoiceConfirm = () => {
    if (!selectedJob) return;
    setInvoiceOpen(false);
    const q = new URLSearchParams({
      customerName,
      title: selectedJob.title || '',
      description: selectedJob.description || '',
      price: String(selectedJob.price ?? ''),
      selectedDate: selectedJob.selectedDate || '',
      selectedTime: selectedJob.selectedTime || '',
      jobId: String(selectedJob.id),
    }).toString();
    navigate(`/create-invoice?${q}`);
  };

  const handleInvoiceCancel = async () => {
    setInvoiceOpen(false);
    // reload page 1 for freshness
    setJobs([]);
    setPage(1);
    await loadJobs(1);
  };

  const handleQuoteConfirm = () => {
    if (!selectedJob) return;
    setQuoteOpen(false);
    const q = new URLSearchParams({
      id: String(selectedJob.id),
      customerId: String(selectedJob.customerId ?? ''),
      customerName: selectedJob.customerName ?? '',
      title: selectedJob.title ?? '',
      description: selectedJob.description ?? '',
      price: String(selectedJob.price ?? ''),
    }).toString();
    navigate(`/create-quote?${q}`);
  };

  const handleQuoteCancel = async () => {
    setQuoteOpen(false);
    // No need to refresh jobs list since we already updated it
  };

  const handleUndoPress = async (job) => {
    try {
      const token = getToken();
      await axios.put(
        `${API_BASE_URL}/jobs/edit/${job.id}`,
        { ...job, completed: 'no' },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      // refresh current list from page 1 (simple + guaranteed fresh)
      setJobs([]);
      setPage(1);
      await loadJobs(1);
    } catch (err) {
      console.error('Error undoing job completion:', err);
      alert('Failed to undo job completion. Please try again later.');
    }
  };

  const formatDateTime = (selectedDate, selectedTime) => {
    if (!selectedDate || !selectedTime) return { formattedDate: '', formattedTime: '' };
    const date = new Date(`${selectedDate}T${selectedTime}Z`);
    const formattedDate = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return { formattedDate, formattedTime };
  };

  if (!customerId) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text>No customer selected.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={styles.header}>{`${customerName}'s Jobs`}</Text>
      <View style={styles.totalJobsContainer}>
        <Text style={styles.totalJobsText}>{`Total Jobs: ${jobs.length}`}</Text>
      </View>

      {jobs.length > 0 ? (
        <FlatList
          data={jobs}
          keyExtractor={(item, index) => String(item.id ?? index)}
          renderItem={({ item }) => {
            const { formattedDate, formattedTime } = formatDateTime(item.selectedDate, item.selectedTime);
            return (
              <TouchableOpacity
                onPress={() => navigate(`/job-details?id=${encodeURIComponent(item.id)}`)}
                style={[styles.card, { paddingTop: 40 }]}
              >
                <Text
                  style={[
                    styles.topLeftLabel,
                    {
                      backgroundColor: item.visitType === 'job' ? 'green' : 'lightblue',
                      color: item.visitType === 'job' ? 'white' : 'black',
                    },
                  ]}
                >
                  {item.visitType === 'job' ? 'Job' : 'Quote'}
                </Text>
                <Text style={styles.jobTitle}>{item.title}</Text>
                <View style={styles.dateContainer}>
                  <MdCalendarToday size={16} color="#007AFF" aria-hidden />
                  <Text style={styles.dateText}>{formattedDate} @ {formattedTime}</Text>
                </View>

                <Text style={styles.jobDetail}>{`Description: ${item.description}`}</Text>

                <View style={styles.addressContainer}>
                  <Text style={styles.jobDetail}>{`Address: ${item.address}`}</Text>
                  <TouchableOpacity
                    onPress={() => {
                      const address = `${item.address}, ${item.city}, ${item.state}, ${item.zip}`;
                      const url = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
                      window.open(url, '_self');
                    }}
                  >
                    <View style={styles.iconTextContainer}>
                      <IoNavigate size={16} color="blue" aria-hidden />
                      <Text style={styles.navigateText}>Map</Text>
                    </View>
                  </TouchableOpacity>
                </View>

                <Text style={styles.jobDetail}>{`${item.city}, ${item.state} ${item.zip}`}</Text>
                {canSeePrices() && <Text style={styles.jobDetail}>{`Price: $${item.price}`}</Text>}

                {item.completed === 'no' ? (
                  <TouchableOpacity
                    style={[
                      styles.completeButton,
                      !hasJobWritePermission() && styles.disabledButton
                    ]}
                    onPress={() => hasJobWritePermission() && handleCompletePress(item)}
                    disabled={!hasJobWritePermission()}
                  >
                    <Text style={styles.completeButtonText}>
                      {hasJobWritePermission() ? '✔ Complete' : 'Incomplete'}
                    </Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.completedContainer}>
                    <Text style={styles.completedText}>✔ Completed</Text>
                    {hasJobWritePermission() && (
                      <TouchableOpacity onPress={() => handleUndoPress(item)}>
                        <Text style={styles.undoText}>Undo</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </TouchableOpacity>
            );
          }}
          ListFooterComponent={
            hasMore ? (
              <TouchableOpacity
                style={[styles.card, { alignItems: 'center' }]}
                onPress={handleLoadMore}
                disabled={loading}
              >
                <Text style={{ fontWeight: 'bold' }}>{loading ? 'Loading…' : 'Load more'}</Text>
              </TouchableOpacity>
            ) : null
          }
        />
      ) : (
        <Text style={styles.noJobsText}>No jobs found for this customer.</Text>
      )}

      {/* Completion confirm overlay */}
      {completionOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Complete Job</Text>
            <Text style={styles.modalMessage}>Has this job been completed?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleCompletionConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={() => setCompletionOpen(false)}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Create invoice prompt overlay */}
      {invoiceOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Create Invoice</Text>
            <Text style={styles.modalMessage}>Would you like to create an invoice now?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleInvoiceConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={handleInvoiceCancel}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Create quote prompt overlay */}
      {quoteOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Create Quote</Text>
            <Text style={styles.modalMessage}>Would you like to create a quote to send to the customer?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleQuoteConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={handleQuoteCancel}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, paddingBottom: 100, backgroundColor: '#fff' },
  header: { fontSize: 20, fontWeight: 'bold', marginBottom: 20, textAlign: 'center' },
  totalJobsContainer: { alignSelf: 'flex-end', marginBottom: 10 },
  totalJobsText: { fontSize: 16, fontWeight: 'bold', color: '#007AFF' },

  card: {
    backgroundColor: '#f0f0f0',
    padding: 15,
    borderRadius: 8,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  jobTitle: { fontWeight: 'bold', fontSize: 18, marginBottom: 4, textDecorationLine: 'underline' },
  dateContainer: { position: 'absolute', alignItems: 'center', top: 10, right: 10, flexDirection: 'row' },
  dateText: { marginLeft: 5, fontSize: 14, color: 'green', fontWeight: 'bold' },
  timeText: { marginLeft: 5, fontSize: 14, color: 'green', fontWeight: 'bold' },

  jobDetail: { fontSize: 14, color: '#444', marginBottom: 4 },
  noJobsText: { fontSize: 14, color: '#888' },

  addressContainer: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  iconTextContainer: { flexDirection: 'row', alignItems: 'center' },
  navigateText: { marginLeft: 5, fontSize: 14, color: 'blue', textDecorationLine: 'underline' },

  completeButton: { marginTop: 0, backgroundColor: '#4CAF50', padding: 10, borderRadius: 5, alignItems: 'center', alignSelf: 'flex-end' },
  disabledButton: {
    backgroundColor: '#cccccc',
    opacity: 0.6,
  },
  completeButtonText: { color: 'white', fontWeight: 'bold' },
  completedContainer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginTop: 10 },
  completedText: { fontSize: 14, color: '#4CAF50', marginRight: 10 },
  undoText: { fontSize: 14, color: '#2196F3' },

  // web-safe overlays
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 1000,
  },
  topLeftLabel: {
    position: 'absolute',
    top: 10,
    left: 10,
    backgroundColor: 'lightblue',
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 2,
    paddingBottom: 2,
    borderRadius: 4,
    fontWeight: 'bold',
    color: 'black',
  },
  overlayCard: {
    width: 'min(560px, 100%)',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  modalTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 10 },
  modalMessage: { fontSize: 16, marginBottom: 20, textAlign: 'center' },
  modalButtons: { flexDirection: 'row', justifyContent: 'space-between', width: '100%' },
  modalButton: { flex: 1, marginHorizontal: 5, backgroundColor: '#4CAF50', padding: 10, borderRadius: 5, alignItems: 'center' },
  modalButtonText: { color: 'white', fontWeight: 'bold' },
});
